import { useContext, useEffect, useState } from 'preact/hooks';

import { CountryCodeSelectorContext } from '@/context/CountryCodeSelectorContext';
import { useLoyaltyProgramContext } from '@/context/LoyaltyProgramContext';
import { useNavigation } from '@/context/NavigationContext';
import { usePlatformHandlers } from '@/context/PlatformContext';
import { useWidgetConfig } from '@/context/WidgetConfigContext';
import { getDeviceData } from '@/helpers/functions/deviceDetection';
import {
  addPlusSignToCountryCode,
  getPhoneNumberValidationErrorMessage,
  isPhoneNumberValidForRegion,
} from '@/helpers/functions/phone';
import { E_AppRoutes } from '@/types/Routes';
import { E_CustomerTitle, E_PreferredLanguage } from '@/types/Shopify';
import { E_RegistrationStep, type T_RegistrationStep } from '@/views/UnregisteredCustomer/enums';
import type { T_ExistingCustomer, T_FormErrors } from '@/views/UnregisteredCustomer/types';

export interface I_UseUnregisteredCustomerFormReturn {
  step: T_RegistrationStep;
  setStep: (step: T_RegistrationStep) => void;
  existingCustomer: T_ExistingCustomer | null;
  setExistingCustomer: (c: T_ExistingCustomer | null) => void;
  birthDate: string | undefined;
  setBirthDate: (d: string | undefined) => void;
  phoneNumber: string;
  setPhoneNumber: (p: string) => void;
  title: E_CustomerTitle | undefined;
  setTitle: (t: E_CustomerTitle | undefined) => void;
  firstName: string;
  setFirstName: (f: string) => void;
  lastName: string;
  setLastName: (l: string) => void;
  nationality: string | undefined;
  setNationality: (n: string | undefined) => void;
  isSubmitting: boolean;
  setIsSubmitting: (b: boolean) => void;
  isSearching: boolean;
  errors: T_FormErrors;
  setErrors: (e: T_FormErrors | ((prev: T_FormErrors) => T_FormErrors)) => void;
  onPhoneSubmit: () => Promise<void>;
  onExistingCustomerChoice: (useAccount: boolean) => void;
  onRegister: () => Promise<void>;
}

export function useUnregisteredCustomerForm(): I_UseUnregisteredCustomerFormReturn {
  const { registerCustomer, searchExistingCustomer } = usePlatformHandlers();
  const { navigateTo } = useNavigation();
  const { selectedCountry } = useContext(CountryCodeSelectorContext);
  const { setLoyaltyCardStatus, customerHasAddedLoyaltyCard } = useLoyaltyProgramContext();

  const {
    countryCode: savedCountryCode,
    phoneNumber: savedPhoneNumber,
    firstName: savedFirstName,
    lastName: savedLastName,
    showNationality,
  } = useWidgetConfig();

  const [step, setStep] = useState<T_RegistrationStep>(E_RegistrationStep.PHONE);
  const [existingCustomer, setExistingCustomer] = useState<T_ExistingCustomer | null>(null);
  const [birthDate, setBirthDate] = useState<string | undefined>(undefined);
  const [phoneNumber, setPhoneNumber] = useState(savedPhoneNumber);
  const [title, setTitle] = useState<E_CustomerTitle | undefined>(undefined);
  const [firstName, setFirstName] = useState(savedFirstName);
  const [lastName, setLastName] = useState(savedLastName);
  const [nationality, setNationality] = useState<string | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [errors, setErrors] = useState<T_FormErrors>({});

  useEffect(() => {
    if (selectedCountry?.dialCode) {
      setErrors((prev) => ({ ...prev, countryCode: undefined }));
    }
  }, [selectedCountry?.dialCode]);

  function onExistingCustomerChoice(useAccount: boolean) {
    if (useAccount && existingCustomer) {
      setFirstName(existingCustomer.firstName);
      setLastName(existingCustomer.lastName ?? '');
      if (
        existingCustomer.title &&
        Object.values(E_CustomerTitle).includes(existingCustomer.title as E_CustomerTitle)
      ) {
        setTitle(existingCustomer.title as E_CustomerTitle);
      }
      if (existingCustomer.birthDate) setBirthDate(existingCustomer.birthDate);
    } else {
      setExistingCustomer(null);
    }
    setStep(E_RegistrationStep.FORM);
  }

  async function onPhoneSubmit() {
    const cleanedCountryCode = addPlusSignToCountryCode(
      selectedCountry?.dialCode ?? savedCountryCode ?? '+974',
    );
    const trimmedPhoneNumber = phoneNumber.trim();

    const newErrors: T_FormErrors = {};
    if (!trimmedPhoneNumber) {
      newErrors.phoneNumber = 'Phone number is required.';
    } else if (
      cleanedCountryCode &&
      !isPhoneNumberValidForRegion(trimmedPhoneNumber, cleanedCountryCode)
    ) {
      newErrors.phoneNumber = getPhoneNumberValidationErrorMessage();
    }
    if (!cleanedCountryCode) newErrors.countryCode = 'Country code is required.';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});

    setIsSearching(true);

    try {
      if (!searchExistingCustomer) {
        setStep(E_RegistrationStep.FORM);
        return;
      }
      const result = await searchExistingCustomer({
        phoneNumber: trimmedPhoneNumber,
        countryCode: cleanedCountryCode,
      });
      if (result?.exists && result.customer) {
        setExistingCustomer(result.customer);
        setStep(E_RegistrationStep.EXISTING_CONFIRM);
      } else {
        setStep(E_RegistrationStep.FORM);
      }
    } catch (err) {
      console.error('Error searching existing customer:', err);
      setStep(E_RegistrationStep.FORM);
    } finally {
      setIsSearching(false);
    }
  }

  async function performRegistration() {
    const cleanedCountryCode = addPlusSignToCountryCode(
      selectedCountry?.dialCode ?? savedCountryCode,
    );
    const trimmedFirstName = firstName?.trim() || '';
    const trimmedPhoneNumber = phoneNumber.trim();

    if (!registerCustomer) return;

    try {
      const data = await registerCustomer({
        phoneNumber: trimmedPhoneNumber,
        countryCode: cleanedCountryCode,
        language: E_PreferredLanguage.ENGLISH,
        title,
        birthDate,
        firstName: trimmedFirstName,
        lastName: lastName?.trim(),
        nationality,
        deviceData: getDeviceData(),
      });
      const cardStatus = data?.data?.loyaltyCardStatus;
      if (cardStatus) {
        setLoyaltyCardStatus(cardStatus);
        if (!customerHasAddedLoyaltyCard(cardStatus)) {
          navigateTo(E_AppRoutes.POST_REGISTRATION);
          return;
        }
      }
      navigateTo(E_AppRoutes.REGISTERED_CUSTOMER);
    } catch (err) {
      console.error('Failed to sync. Try again.', err);
      setErrors({
        phoneNumber: 'This phone number is already in use. Please try a different one.',
      });
    }
  }

  async function onRegister() {
    const cleanedCountryCode = addPlusSignToCountryCode(
      selectedCountry?.dialCode ?? savedCountryCode,
    );
    const trimmedFirstName = firstName?.trim() || '';
    const trimmedPhoneNumber = phoneNumber.trim();

    const newErrors: T_FormErrors = {};
    if (!trimmedPhoneNumber) {
      newErrors.phoneNumber = 'Phone number is required.';
    } else if (
      cleanedCountryCode &&
      !isPhoneNumberValidForRegion(trimmedPhoneNumber, cleanedCountryCode)
    ) {
      newErrors.phoneNumber = getPhoneNumberValidationErrorMessage();
    }
    if (!cleanedCountryCode) newErrors.countryCode = 'Country code is required.';
    if (!savedFirstName && !trimmedFirstName) newErrors.firstName = 'First name is required.';
    if (!title) newErrors.title = 'Title is required.';
    if (showNationality && !nationality) newErrors.nationality = 'Nationality is required.';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    if (Boolean(errors?.birthDate?.length)) return;

    setErrors({});

    setIsSubmitting(true);
    await performRegistration();
    setIsSubmitting(false);
  }

  return {
    step,
    setStep,
    existingCustomer,
    setExistingCustomer,
    birthDate,
    setBirthDate,
    phoneNumber,
    setPhoneNumber,
    title,
    setTitle,
    firstName: firstName ?? '',
    setFirstName,
    lastName: lastName ?? '',
    setLastName,
    nationality,
    setNationality,
    isSubmitting,
    setIsSubmitting,
    isSearching,
    errors,
    setErrors,
    onPhoneSubmit,
    onExistingCustomerChoice,
    onRegister,
  };
}
